import type {
  AgeGroup,
  AppointmentRole,
  BenchJobState,
  BenchStepName,
  BenchStepStatus,
  ClubStatus,
  KitCondition,
  KitReason,
  LeagueTier,
  InterventionItemRead,
  InterventionState,
  MaintenanceAction,
  MaintenanceStatus,
  PartOutcome,
  MatchAnalysisState,
  MatchLeg,
  MatchStatus,
  PlayerFoot,
  RecordingStatus,
  SegmentQuality,
  PlayerPosition,
  RegistrationRequestStatus,
  Sex,
  StaffMemberRole,
  TeamGender,
  UserSubscription,
} from '@volee/api-client'

// Domain enum → label maps, SOURCE language. Each is a complete Record, so a backend value
// without a label does not compile. FIGC categories keep their Italian VALUES: they are data.

/* The three roles a club appoints. The type comes from the contract and is no longer
 * restated here (rule 5), so one added on the backend without a label is a compile error. */
export type { AppointmentRole }

export const EN_APPOINTMENT_ROLES: Record<AppointmentRole, string> = {
  official: 'Club official',
  team_official: 'Team official',
  coach: 'Coach',
}

export const EN_PLAYER_POSITIONS: Record<PlayerPosition, string> = {
  goalkeeper: 'Goalkeeper',
  defender: 'Defender',
  midfielder: 'Midfielder',
  forward: 'Forward',
}

/* Derived from the contract, like PlayerPosition above. */
export type { PlayerFoot }

export const EN_PLAYER_FEET: Record<PlayerFoot, string> = {
  left: 'Left',
  right: 'Right',
  both: 'Both',
}

/* FIGC categories: the values are Italian because they are the federation's names. The
 * type comes from the contract, like ClubStatus below, so a category added on the backend
 * without its label is a compile error. */
export type { AgeGroup, PlayerPosition, Sex, TeamGender }

export const EN_AGE_GROUPS: Record<AgeGroup, string> = {
  seniores: 'Seniores',
  juniores: 'Juniores',
  allievi: 'Allievi',
  giovanissimi: 'Giovanissimi',
  esordienti: 'Esordienti',
  pulcini: 'Pulcini',
}

/* Which of the two a team plays: the FIGC runs the women's game as its own championships. */
export const EN_TEAM_GENDERS: Record<TeamGender, string> = {
  male: 'Men',
  female: 'Women',
  open: 'Mixed',
}

/* A person's sex, which is not the sentence a team's gender is: separate labels over the
 * same two values. */
export const EN_SEXES: Record<Sex, string> = {
  male: 'Male',
  female: 'Female',
}

/* Where a club sits in the activation flow. The type comes from the contract rather than
 * being restated, so a status added on the backend without a label is a compile error. */
export const EN_CLUB_STATUSES: Record<ClubStatus, string> = {
  lead: 'Lead',
  in_review: 'In review',
  active: 'Active',
  suspended: 'Suspended',
  archived: 'Archived',
}

/* Where a sign-up request stands. `submitted` and `needs_integration` are the operator's
 * queue; the other two are settled. */
export const EN_REGISTRATION_REQUEST_STATUSES: Record<RegistrationRequestStatus, string> = {
  submitted: 'Awaiting review',
  needs_integration: 'Needs more',
  approved: 'Approved',
  rejected: 'Rejected',
}

/* Which back-office role someone holds. From the contract and not re-derived from `Me`:
 * two derivations of one Literal are identical until they are not, and `rolesOf` collapses
 * all three into `staff`, so this map is the only place the distinction reaches the screen. */
export type StaffRole = StaffMemberRole

export const EN_STAFF_ROLES: Record<StaffRole, string> = {
  admin: 'Administrator',
  operations: 'Operations',
  support: 'Support',
}

/* A league category's classification level, the FIGC's own scale from a comune's
 * championship up to the region's. The type comes from the contract, like the FIGC
 * categories above, so a tier added on the backend without a label is a compile error. */
export type { LeagueTier }

export const EN_LEAGUE_TIERS: Record<LeagueTier, string> = {
  provincial: 'Provincial',
  interprovincial: 'Interprovincial',
  regional: 'Regional',
  elite: 'Elite',
}

/* The bulletin's own adjective, agreeing with the category it describes — plural in
 * Italian ("Provinciali"), where the source language has no such agreement to carry. */
export const EN_LEAGUE_TIER_ADJECTIVES: Record<LeagueTier, string> = {
  provincial: 'Provincial',
  interprovincial: 'Interprovincial',
  regional: 'Regional',
  elite: 'Elite',
}

export type GuardianRelationship = 'mother' | 'father' | 'guardian'

export const EN_GUARDIAN_RELATIONSHIPS: Record<GuardianRelationship, string> = {
  mother: 'Mother',
  father: 'Father',
  guardian: 'Guardian',
}

/** Shown in place of a value no catalogue knows. */
export const EN_UNKNOWN_VALUE = 'Not specified'

/* A fixture's state. `played` is not writable from the back-office — the score comes from
 * the video — but a seeded or imported row wears it, so the label exists. */
export type { MatchStatus }

export const EN_MATCH_STATUSES: Record<MatchStatus, string> = {
  scheduled: 'Scheduled',
  played: 'Played',
  postponed: 'Postponed',
}

/* Four and not two: small groups go round three or four times (db/models/matches.py). */
export type { MatchLeg }

export const EN_MATCH_LEGS: Record<MatchLeg, string> = {
  first: 'First leg',
  second: 'Second leg',
  third: 'Third leg',
  fourth: 'Fourth leg',
}

/* A recording session, not a file. `in_progress` is the normal state for hours: over a SIM
 * the upload of one match takes most of a day. */
export type { RecordingStatus, SegmentQuality }

/* One side's convocazione, as the back office reads it. Ours, not the backend's: derived from
 * `published_at` and the call-ups (`teamSheetState` in data/matches.ts). */
export const TEAM_SHEET_STATES = ['none', 'draft', 'published'] as const
export type TeamSheetState = (typeof TEAM_SHEET_STATES)[number]

export const EN_TEAM_SHEET_STATES: Record<TeamSheetState, string> = {
  none: 'No call-up',
  draft: 'Draft',
  published: 'Published',
}

/* Where a fixture stands at the analysis provider. Only what UbiTrack told us: nothing asks it. */
export type { MatchAnalysisState }

export const EN_MATCH_ANALYSIS_STATES: Record<MatchAnalysisState, string> = {
  not_submitted: 'Not sent',
  submitted: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  completed: 'Analysed',
  failed: 'Failed',
  imported: 'Results in',
}

export const EN_RECORDING_STATUSES: Record<RecordingStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'Arriving',
  complete: 'Complete',
  incomplete: 'Incomplete',
  failed: 'Failed',
  processed: 'Processed',
}

/* What the camera emits. Named for what they are worth to a reader — one is watchable at an
 * eighth of the bytes — and not for the profile that produced them. */
export const EN_SEGMENT_QUALITIES: Record<SegmentQuality, string> = {
  main: 'High resolution',
  sub: 'Substream',
}

export const EN_BENCH_JOB_STATES: Record<BenchJobState, string> = {
  offered: 'Waiting for the box',
  accepted: 'Being prepared',
  saved: 'Being prepared',
  done: 'Ready',
  failed: 'Stopped on an error',
  taken_elsewhere: 'Taken by another environment',
}

export const EN_BENCH_STEPS: Record<BenchStepName, string> = {
  router: 'Router: password, SIM and Wi-Fi',
  camera: 'Camera: password and video',
  pi: 'Gateway: name, password and Tailscale',
  cloud: 'Connection to the cloud',
}

export const EN_BENCH_STEP_STATUSES: Record<BenchStepStatus, string> = {
  running: 'Running',
  ok: 'Done',
  failed: 'Failed',
}

export const EN_MAINTENANCE_ACTIONS: Record<MaintenanceAction, string> = {
  router_wifi_off: 'Turn the router’s Wi-Fi off',
  camera_dhcp_reservation: 'Redo the camera’s reservation',
  camera_rtsp_on: 'Turn RTSP back on',
  camera_reboot: 'Restart the camera',
  camera_apply_profile: 'Reapply the camera profile',
  read_inventory: 'Re-read router and camera',
  adopt_router: 'Take the new router in',
  adopt_camera: 'Take the new camera in',
}

export const EN_MAINTENANCE_STATUSES: Record<MaintenanceStatus, string> = {
  requested: 'Waiting',
  running: 'Running',
  ok: 'Done',
  failed: 'Failed',
  declined: 'Declined',
  expired: 'Expired',
}

/* The kit's condition, in the glossary's words (CONTEXT.md § «I kit dei campi»). */
export const EN_KIT_CONDITIONS: Record<KitCondition, string> = {
  never_seen: 'Never seen',
  silent: 'Silent',
  check: 'To check',
  ok: 'Working',
}

export interface KitReasonText {
  readonly title: string
  readonly hint: string
}

export const EN_KIT_REASONS: Record<KitReason, KitReasonText> = {
  no_cameras: {
    title: 'No camera',
    hint: 'The kit records nothing: add the camera in the cabinet.',
  },
  no_main_stream: {
    title: 'A camera without a main stream',
    hint: 'Without the main stream a match is recorded and never uploaded.',
  },
  stream_stuck: {
    title: 'A stream is not producing',
    hint: 'It restarts without producing video: check that RTSP is on in the camera.',
  },
  clock_unsynced: {
    title: 'Clock not synchronised',
    hint: 'Recordings could start at the wrong time.',
  },
}

/* Stripe's states, worded as a plan (never "subscription" on screen). The three that cannot
 * come back all read as ended. */
export const EN_SUBSCRIPTION_STATUSES: Record<UserSubscription['status'], string> = {
  active: 'Active',
  trialing: 'On trial',
  past_due: 'Payment overdue',
  paused: 'Paused',
  incomplete: 'Awaiting payment',
  incomplete_expired: 'Ended',
  canceled: 'Ended',
  unpaid: 'Ended',
}

export const EN_INTERVENTION_STATES: Record<InterventionState, string> = {
  open: 'Open',
  in_progress: 'In progress',
  closed: 'Closed',
  cancelled: 'Cancelled',
}

export const EN_PART_OUTCOMES: Record<PartOutcome, string> = {
  reusable: 'Reusable',
  broken: 'Broken',
  disposed: 'Disposed of',
}

export const EN_INTERVENTION_PARTS: Record<InterventionItemRead['part'], string> = {
  gateway: 'Gateway',
  router: 'Router',
  sim: 'SIM',
  camera: 'Camera',
}
